import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { isHelpCenterAIAnswersEAPEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { createSelector } from 'react-sweet-state';
import type { UserRequestsCounts } from 'state/persisted/user-requests-counts';
import { getOrganisations } from 'state/selectors/organisations';
import { getUserRequestsCounts } from 'state/selectors/user-requests-counts';
import { getProductIdentifierAttributes } from 'unified-help-center-alpha/src/utils/analytics/get-product-identifier-attributes';
import { AvatarMenu } from 'view/layout/avatar-menu';
import { RequestsMenu } from 'view/layout/requests-menu';
import type { Organisations } from 'view/layout/requests-menu';
import Button from '@atlaskit/button';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ExperienceType } from '@atlassian/conversation-assistant';
import { BackButton } from '@atlassian/help-center-common-component/back-button';
import { getAnalyticsWebClient } from '@atlassian/help-center-common-util/analytics/client';
import { history } from '@atlassian/help-center-common-util/history';
import { reload } from '@atlassian/help-center-common-util/location';
import messages from './messages';
interface Props {
    userRequest: UserRequestsCounts;
    organisations: Organisations[];
}

export const ConversationNavBar = (props: Props) => {
    const analyticsClient = getAnalyticsWebClient().getInstance();
    const { formatMessage } = useIntl();

    const onBackButtonClick = useCallback(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        analyticsClient.sendUIEvent({
            source: 'uhConversationScreen',
            action: 'clicked',
            actionSubject: 'backButton',
            actionSubjectId: 'backToHelpCenterHome',
            attributes: {
                ...getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP),
            },
        });
        if (!isHelpCenterAIAnswersEAPEnabled()) {
            reload();
        } else {
            history.push('/portals');
        }
    }, [analyticsClient]);

    return (
        <Box xcss={navBarStyles}>
            <Inline alignBlock="center" alignInline="center" grow="fill">
                <Box xcss={navBarBackButtonStyles}>
                    {isHelpCenterAIAnswersEAPEnabled() ? (
                        <Button
                            appearance="subtle"
                            onClick={onBackButtonClick}
                            iconBefore={
                                <ChevronLeftLargeIcon
                                    label={formatMessage(messages.backButtonText)}
                                    aria-hidden="true"
                                    size="medium"
                                />
                            }
                            data-test-id="back-button"
                        >
                            {formatMessage(messages.backButtonText)}
                        </Button>
                    ) : (
                        <BackButton actionSubjectId="closeSearch" onClick={onBackButtonClick} />
                    )}
                </Box>
                {isHelpCenterAIAnswersEAPEnabled() && (
                    <Box xcss={navBarRequestMenuStyles}>
                        <Inline>
                            <RequestsMenu
                                approvalCount={props.userRequest.approvalCount || 0}
                                pendingApprovalCount={props.userRequest.pendingApprovalCount || 0}
                                myRequestCount={props.userRequest.myRequestCount || 0}
                                organisations={props.organisations}
                            />
                            <AvatarMenu />
                        </Inline>
                    </Box>
                )}
            </Inline>
        </Box>
    );
};

const selector = createSelector(getUserRequestsCounts, getOrganisations, (userRequest, organisations) => ({
    userRequest,
    organisations,
}));

export default connect(selector, {})(ConversationNavBar);

const navBarStyles = xcss({
    display: 'flex',
    position: 'fixed',
    height: token('space.600', '56px'),
    width: '100%',
    backgroundColor: 'elevation.surface.overlay',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: token('color.border', colors.N30),
    zIndex: 'modal',
});

const navBarBackButtonStyles = xcss({
    position: 'fixed',
    left: token('space.050', '4px'),
    [media.above.sm]: {
        left: token('space.200', '16px'),
    },
    [media.above.xs]: {
        left: token('space.100', '8px'),
    },
});

const navBarRequestMenuStyles = xcss({
    position: 'fixed',
    right: token('space.050', '4px'),
});
